import moment from 'moment-timezone';
import $ from 'legacy/jquery';
import t from 'shared/utils/translation';
import Tooltip from 'legacy/tooltip';
import './availability_scheduling_window.css';

const FLYOUT_BODY_MARKER = 'has-scheduling-window-flyout';
const HOVER_TARGET_SELECTOR =
  '.fc-bgevent.out-of-scheduling-window-bg, .fc-day-header.out-of-scheduling-window';
const HOVER_DELAY_MS = 100;
const TOOLTIP_BELOW_HEADER_PX = 10;
const BLOCKED_EVENT_DEFAULTS = {
  rendering: 'background',
  overlap: false,
  _blockedByWindow: true,
  className: 'out-of-scheduling-window-bg',
};

export default class AvailabilitySchedulingWindow {
  constructor(container, timeZoneId) {
    this.container = container;
    this.timeZoneId = timeZoneId;
    this.start = windowBoundaryInTimeZone(
      container.data('scheduling-window-start'),
      timeZoneId
    );
    this.end = windowBoundaryInTimeZone(
      container.data('scheduling-window-end'),
      timeZoneId
    );
    this._initialized = false;
    this._showTimer = null;
  }

  applyToOptions(options) {
    options.viewRender = () => this.styleCells();
    options.selectOverlap = (event) => !event._blockedByWindow;
    options.eventAfterRender = (event, element) => {
      if (event._blockedByWindow) {
        element.attr(
          'data-description',
          t(
            'candidate_profile.availability_modal.scheduling_window.unavailable_tooltip'
          )
        );
      }
    };
  }

  initInteractions() {
    if (this._initialized) return;
    this._initialized = true;

    document.body.classList.add(FLYOUT_BODY_MARKER);
    this.ensureTooltipFlyoutMounted();
    this.bindTooltipHoverHandlers();
    this.styleCells();
  }

  ensureTooltipFlyoutMounted() {
    if (!Tooltip.flyout().length) {
      $('body').append($(Tooltip.flyoutHtml));
    }
  }

  bindTooltipHoverHandlers() {
    this.container.on('mouseenter', HOVER_TARGET_SELECTOR, (event) => {
      const target = event.currentTarget;
      this.clearShowTimer();
      this._showTimer = setTimeout(() => {
        this.showTooltipAtColumnHeader(target);
      }, HOVER_DELAY_MS);
    });

    this.container.on('mouseleave', HOVER_TARGET_SELECTOR, () => {
      this.clearShowTimer();
      Tooltip.flyout().hide();
    });
  }

  clearShowTimer() {
    if (this._showTimer) {
      clearTimeout(this._showTimer);
      this._showTimer = null;
    }
  }

  showTooltipAtColumnHeader(target) {
    const $target = $(target);
    const text = $target.attr('data-description');
    if (!text) return;

    const $header = this.findColumnHeader($target);
    if (!$header || !$header.length) return;

    Tooltip.setText(text);
    const $flyout = Tooltip.flyout();
    const headerOffset = $header.offset();
    $flyout.css({
      top: headerOffset.top + $header.outerHeight() + TOOLTIP_BELOW_HEADER_PX,
      left:
        headerOffset.left + $header.outerWidth() / 2 - $flyout.outerWidth() / 2,
    });
    $flyout.show();
  }

  findColumnHeader($target) {
    if ($target.hasClass('fc-day-header')) {
      return $target;
    }
    const $td = $target.closest('td');
    if (!$td.length) return $();
    const colIndex = $td.prevAll('td:not(.fc-axis)').length;
    return this.container.find('.fc-day-header').eq(colIndex);
  }

  styleCells() {
    const windowStart = this.start.clone().startOf('day');
    const windowEnd = this.end.clone().startOf('day');
    const text = t(
      'candidate_profile.availability_modal.scheduling_window.unavailable_tooltip'
    );

    this.container.find('.fc-day-header, .fc-day').each(function () {
      const $cell = $(this);
      const dateStr = $cell.data('date');
      if (!dateStr) return;

      const day = moment.utc(dateStr).startOf('day');
      const isOutOfWindow = day.isBefore(windowStart) || day.isAfter(windowEnd);
      $cell.toggleClass('out-of-scheduling-window', isOutOfWindow);

      if (!$cell.hasClass('fc-day-header')) return;

      if (isOutOfWindow) {
        $cell.attr('data-description', text);
      } else {
        $cell.removeAttr('data-description');
      }
    });

    this.addOutOfScheduleWindowBgEvents();
  }

  addOutOfScheduleWindowBgEvents() {
    this.removeExistingBlockedEvents();

    const blockedEvents = this.computeBlockedEventsForCurrentView();
    if (blockedEvents.length > 0) {
      this.container.fullCalendar('addEventSource', blockedEvents);
    }
  }

  removeExistingBlockedEvents() {
    this.container.fullCalendar(
      'removeEvents',
      (event) => event._blockedByWindow === true
    );
  }

  computeBlockedEventsForCurrentView() {
    const view = this.container.fullCalendar('getView');
    if (!view) return [];

    const viewStart = moment(view.start);
    const viewEnd = moment(view.end);
    const events = [];

    const current = viewStart.clone().startOf('day');
    while (current.isBefore(viewEnd)) {
      events.push(...this.blockedEventsForDay(current));
      current.add(1, 'day');
    }
    return events;
  }

  blockedEventsForDay(day) {
    const dayStart = day.clone();
    const dayEnd = day.clone().add(1, 'day');
    const windowStart = this.start;
    const windowEnd = this.end;

    if (
      dayEnd.isSameOrBefore(windowStart) ||
      dayStart.isSameOrAfter(windowEnd)
    ) {
      return [buildBlockedEvent(dayStart, dayEnd)];
    }

    const events = [];
    if (windowStart.isAfter(dayStart) && windowStart.isBefore(dayEnd)) {
      events.push(
        buildBlockedEvent(dayStart, windowStart.clone().subtract(1, 'minute'))
      );
    }
    if (windowEnd.isAfter(dayStart) && windowEnd.isBefore(dayEnd)) {
      events.push(
        buildBlockedEvent(windowEnd.clone().add(1, 'minute'), dayEnd)
      );
    }
    return events;
  }
}

function buildBlockedEvent(start, end) {
  return { start, end, ...BLOCKED_EVENT_DEFAULTS };
}

function windowBoundaryInTimeZone(input, timeZoneId) {
  const wallClock = moment.tz(input, timeZoneId).format('YYYY-MM-DDTHH:mm:ss');
  return moment.utc(wallClock);
}
